import {
  get,
  limitToFirst,
  orderByChild,
  query,
  ref,
  startAfter,
} from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { db } from "../lib/firebase";
import {
  ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY,
  ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND,
  getEnterpriseIntakeOrganizationType,
} from "../pages/EnterpriseIntake/enterpriseIntakeFormValues";

import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

const ENTERPRISE_INTAKE_PATH = "enterpriseIntake";

/** Same page size as pre-activation for consistent owner list UX. */
export const ENTERPRISE_INTAKE_PAGE_SIZE = 10;
const PAGE_FETCH = ENTERPRISE_INTAKE_PAGE_SIZE + 1;

const str = (v) => (v == null ? "" : String(v));
const arr = (v) => (Array.isArray(v) ? v : []);

const toOrganizationType = (raw = {}) => {
  if (raw.organizationType === ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND) {
    return ENTERPRISE_INTAKE_ORGANIZATION_TYPE_BRAND;
  }
  if (raw.organizationType === ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY) {
    return ENTERPRISE_INTAKE_ORGANIZATION_TYPE_AGENCY;
  }
  return getEnterpriseIntakeOrganizationType(raw);
};

/**
 * Normalizes RTDB payload to react-hook-form defaultValues for EnterpriseIntake.
 */
export function normalizeEnterpriseIntakeRecord(raw = {}) {
  return {
    organizationType: toOrganizationType(raw),
    fullName: str(raw.fullName),
    corporateEmail: str(raw.corporateEmail),
    companyName: str(raw.companyName),
    jobTitle: str(raw.jobTitle),

    selectedAdleModules: arr(raw.selectedAdleModules),
    scopeBrandConfigsCount: str(raw.scopeBrandConfigsCount),

    module1MonthlyNewImagesCount: str(raw.module1MonthlyNewImagesCount),
    module1MonthlyImageAnimationsCount: str(
      raw.module1MonthlyImageAnimationsCount
    ),
    module1MonthlyVideosCount: str(raw.module1MonthlyVideosCount),
    creativesBufferTypes: arr(raw.creativesBufferTypes),
    creativesBufferPercent: str(raw.creativesBufferPercent),
    creativesBufferPercentOther: str(raw.creativesBufferPercentOther),

    module2MonthlyCampaignsCount: str(raw.module2MonthlyCampaignsCount),
    optimizationFrequency: str(raw.optimizationFrequency),

    platformUsersCount: str(raw.platformUsersCount),
    contractTermMonths: str(raw.contractTermMonths),

    whiteLabelRequirement: str(raw.whiteLabelRequirement),

    mediaManagementAddon: str(raw.mediaManagementAddon),
    monthlyMediaSpendUsd: str(raw.monthlyMediaSpendUsd),

    additionalNotes: str(raw.additionalNotes),
  };
}

export function enterpriseIntakeSnapshotToRows(snapshotVal) {
  if (!snapshotVal || typeof snapshotVal !== "object") return [];
  return Object.entries(snapshotVal)
    .map(([formId, data]) => {
      const n = normalizeEnterpriseIntakeRecord(data);
      return {
        formId,
        fullName: n.fullName,
        corporateEmail: n.corporateEmail,
        companyName: n.companyName,
        jobTitle: n.jobTitle,
        organizationType: n.organizationType,
      };
    })
    .sort((a, b) => {
      const A = `${a.companyName}${a.corporateEmail}`.toLowerCase();
      const B = `${b.companyName}${b.corporateEmail}`.toLowerCase();
      return A.localeCompare(B);
    });
}

/**
 * @param {(rows: ReturnType<typeof enterpriseIntakeSnapshotToRows>) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function listenEnterpriseIntakeSubmissions(onData, onError) {
  const dbRef = ref(db, ENTERPRISE_INTAKE_PATH);
  return onValue(
    dbRef,
    (snap) => {
      try {
        onData(enterpriseIntakeSnapshotToRows(snap.val()));
      } catch (e) {
        store.dispatch(
          LaunchError(e, false, [], {
            functionName: "listenEnterpriseIntakeSubmissions",
            capture: "exception",
          })
        );
        onError?.(e);
      }
    },
    (err) => onError?.(err)
  );
}

export async function fetchEnterpriseIntakeByFormId(formId) {
  if (!formId) return null;
  const dbRef = ref(db, `${ENTERPRISE_INTAKE_PATH}/${formId}`);
  const snap = await get(dbRef);
  if (!snap.exists()) return null;
  return normalizeEnterpriseIntakeRecord(snap.val());
}

export async function fetchEnterpriseIntakePage(cursorKey = null) {
  const baseRef = ref(db, ENTERPRISE_INTAKE_PATH);
  const hasCursor =
    cursorKey?.value != null && typeof cursorKey?.id === "string";
  const pageQuery = hasCursor
    ? query(
        baseRef,
        orderByChild("creationTimeNegative"),
        startAfter(cursorKey.value, cursorKey.id),
        limitToFirst(PAGE_FETCH)
      )
    : query(
        baseRef,
        orderByChild("creationTimeNegative"),
        limitToFirst(PAGE_FETCH)
      );

  const snap = await get(pageQuery);
  if (!snap.exists()) {
    return { rows: [], cursorAfter: null, hasMore: false };
  }

  const entries = [];
  snap.forEach((child) => {
    entries.push([child.key, child.val()]);
  });

  const hasMore = entries.length > ENTERPRISE_INTAKE_PAGE_SIZE;
  const slice = hasMore
    ? entries.slice(0, ENTERPRISE_INTAKE_PAGE_SIZE)
    : entries;

  const rows = slice.map(([formId, data]) => {
    const n = normalizeEnterpriseIntakeRecord(data);
    const creationTimeNegativeRaw = Number(data?.creationTimeNegative);
    const creationTimeNegative = Number.isNaN(creationTimeNegativeRaw)
      ? 0
      : creationTimeNegativeRaw;
    return {
      formId,
      companyName: n.companyName,
      fullName: n.fullName,
      corporateEmail: n.corporateEmail,
      jobTitle: n.jobTitle,
      organizationType: n.organizationType,
      creationTime:
        data?.creationTime ??
        (creationTimeNegative ? -creationTimeNegative : null),
      creationTimeNegative,
    };
  });
  const cursorAfter =
    rows.length > 0
      ? {
          value: rows[rows.length - 1].creationTimeNegative,
          id: rows[rows.length - 1].formId,
        }
      : null;

  return { rows, cursorAfter, hasMore };
}
